import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '@hooks/statehooks';
import { getVehicleImage } from '@services/vehicleservice';
import { updateVehicleImageInfo } from '@store/reducers/vehicleinformation';
import { VehicleImageInfo } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicleimageinfo';
import { Helmet } from 'react-helmet';
import { Vehicle } from '@tsapiclient/zenauto.vehiclequotation.ts.client/interfaces/vehicle';

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallbackImg?: string;
  modelCode?: number;
  trim?: string;
  doors?: number;
  train?: string;
  year?: string;
  vehicle: Vehicle;
}

const VehicleImage: React.FC<ImageProps> = ({
  fallbackImg,
  modelCode,
  trim,
  doors,
  train,
  year,
  src,
  vehicle,
  ...props
}: ImageProps) => {
  const [imgSrc, setSrc] = useState(src);
  const { loading } = props;
  const hasFetchedData = useRef(false);
  const vehicleImageInfo = useAppSelector(
    (state) => state.vehicleInformation.vehicleImageInfo
  );
  const dispatch = useAppDispatch();

  const vehicleImageAltText = () => {
    let altText = `${vehicle.make} ${vehicle.model}`;
    if (vehicle.image) {
      altText = `Example ${altText}`;
    } else {
      altText = `Image for ${altText} coming soon`;
    }
    return altText;
  };

  useEffect(() => {
    if (!hasFetchedData.current) {
      if (modelCode && vehicleImageInfo?.length == 0) {
        getVehicleImage(modelCode, doors, trim, train, year)
          .then((resImage: VehicleImageInfo[]) => {
            dispatch(updateVehicleImageInfo(resImage));
            let url = resImage.length > 0 ? resImage[0].url : '';
            setSrc(url);
          })
          .catch(() => {
            setSrc(src);
          });
      } else {
        let url =
          (vehicleImageInfo as VehicleImageInfo[]).length > 0
            ? (vehicleImageInfo as VehicleImageInfo[])[0].url
            : '';

        if (url) {
          setSrc(url);
        } else {
          setSrc(src);
        }
      }
    }
  }, [modelCode, trim, doors, train]);

  const onError = useCallback(() => {
    if (imgSrc === fallbackImg) {
      return;
    }

    if (imgSrc === src) {
      setSrc(fallbackImg);
      return;
    }

    setSrc(src);
  }, [imgSrc, fallbackImg]);

  useEffect(() => {
    const img = new Image();
    img.src = imgSrc as string;
    img.addEventListener('error', onError);

    return () => {
      img.removeEventListener('error', onError);
    };
  }, [imgSrc, onError]);

  return (
    <>
      <Helmet>
        <link rel="preload" as="image" href={imgSrc}></link>
      </Helmet>
      <img
        {...props}
        alt={vehicleImageAltText()}
        src={imgSrc}
        loading={loading}
        data-testid="vehicle-image"
        height="250"
        width="375"
      />
    </>
  );
};

export default VehicleImage;
